import { useEffect, useRef, useState } from 'react';
import { withTheme } from '@rjsf/core';
import { Theme as RJSFTheme } from '@rjsf/material-ui';
import validator from '@rjsf/validator-ajv8';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useApi, configApiRef, identityApiRef } from '@backstage/core-plugin-api';
import {
  extractQuickConfigSchema,
  QuickConfigExtraction,
} from './quickCdConfig';

const Form = withTheme(RJSFTheme);

export interface QuickCdConfigFormProps {
  /** CD template name resolved from the selected infra's golden path. */
  template: string;
  /** Template version (current_version from the template catalog). */
  version: string;
  /** Branch of the templates repo to fetch the editor schema from. */
  branch: string;
  /** Called on confirm with the quick values ({section: {field: value}}) and
   * the full editor schema already fetched from template-payload, so the
   * caller can build the complete payload without a second fetch. */
  onConfirm: (
    values: Record<string, any>,
    editorSchema: Record<string, any>,
  ) => void;
  /** Called when the step does not apply (no marked fields) or is skipped. */
  onSkip: () => void;
}

/**
 * Quick configuration step of the "Configurar CD desde IaC" flow: fetches the
 * template's editor schema, extracts the fields marked with `x-quick-config`
 * and renders them as a mini-form shown right after selecting an infra. If the
 * schema has no marked fields the step skips itself and the flow continues as
 * before.
 */
export const QuickCdConfigForm = (props: QuickCdConfigFormProps) => {
  const { template, version, branch, onConfirm, onSkip } = props;
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [extraction, setExtraction] = useState<QuickConfigExtraction | null>(
    null,
  );
  const [formData, setFormData] = useState<Record<string, any>>({});
  // onSkip must fire at most once per template/version (auto-skip when the
  // schema has no marked fields).
  const skippedRef = useRef(false);
  // Full editor schema of the current template/version, kept to hand it back
  // on confirm (the headless payload build needs it).
  const editorSchemaRef = useRef<Record<string, any> | null>(null);

  useEffect(() => {
    let cancelled = false;
    skippedRef.current = false;
    const fetchSchema = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = (await identityApi.getCredentials()).token;
        const url = `${backendUrl}/api/devops-api-proxy/template-payload/${template}/${version}?templateBranch=${encodeURIComponent(branch)}&metadata=false`;
        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const payloadData = await response.json();
        const result = extractQuickConfigSchema(payloadData.editorSchema);
        if (cancelled) return;
        editorSchemaRef.current = payloadData.editorSchema ?? null;
        if (!result) {
          // Template without quick-config marks: continue with the normal flow.
          if (!skippedRef.current) {
            skippedRef.current = true;
            onSkip();
          }
          return;
        }
        setExtraction(result);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSchema();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, version, branch]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gridGap={8} p={2}>
        <CircularProgress size={16} />
        <Typography variant="body2">
          Cargando configuración rápida de {template} v{version}...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="warning" style={{ marginBottom: 8 }}>
          No se pudo cargar la configuración rápida ({error}). Puedes continuar
          con el formulario completo.
        </Alert>
        <Button size="small" variant="outlined" onClick={onSkip}>
          Continuar sin configuración rápida
        </Button>
      </Box>
    );
  }

  if (!extraction) {
    // No marked fields: the effect already called onSkip.
    return null;
  }

  return (
    <Box p={2}>
      <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
        Configuración rápida del despliegue
      </Typography>
      <Typography variant="caption" color="textSecondary">
        {template} v{version} — completa estos valores; el resto se rellena con
        los datos de la infraestructura y podrás revisarlo en el formulario
        completo.
      </Typography>
      <Form
        schema={extraction.schema}
        formData={formData}
        onChange={(data: any) => setFormData(data.formData)}
        onSubmit={(data: any) =>
          onConfirm(data.formData ?? {}, editorSchemaRef.current ?? {})
        }
        validator={validator}
        showErrorList={false}
        liveValidate
      >
        <Box display="flex" justifyContent="flex-end" gridGap={8} mt={1}>
          <Button size="small" onClick={onSkip}>
            Omitir
          </Button>
          <Button
            size="small"
            type="submit"
            color="primary"
            variant="contained"
          >
            Continuar
          </Button>
        </Box>
      </Form>
    </Box>
  );
};
